<script lang="ts">
import BenefitCard from '@/components/cards/BenefitCard.vue'
import BenefitButtons from '@/components/inputs/BenefitButtons.vue'
import FaqSection from '@/components/sections/FaqSection.vue'
import HowItWorksSection from '@/components/sections/HowItWorksSection.vue'
import { useBenefitsStore } from '@/stores/benefits'
import { useFaqStore } from '@/stores/faq'
import {
  BenefitCodes,
  BenefitsCategories,
  Themes,
} from '@/utils/helper-objects'
import type {
  Benefit,
  ClaimedCode,
  Nullable,
  TBenefitsCategory,
} from 'src/types'
import { defineComponent } from 'vue'

/**
 * The "Benefits" page.
 */
export default defineComponent({
  components: {
    BenefitCard,
    FaqSection,
    HowItWorksSection,
    BenefitButtons,
  },
  setup() {
    const benefitsStore = useBenefitsStore()

    return {
      BenefitsCategories,
      benefitsStore,
      faqStore: useFaqStore(),
      Themes,
    }
  },
  computed: {
    tasteCardBenefit(): Nullable<Benefit> {
      return this.benefitsStore.benefits.get(BenefitCodes.Tastecard) ?? null
    },
    vueBenefit(): Nullable<Benefit> {
      return this.benefitsStore.benefits.get(BenefitCodes.Vue) ?? null
    },
    rakutenBenefit(): Nullable<Benefit> {
      return this.benefitsStore.benefits.get(BenefitCodes.Rakuten) ?? null
    },
    movieOrdering() {
      if (this.vueBenefit && this.rakutenBenefit) {
        return {
          vue: this.isBenefitDisabled(this.vueBenefit)
            ? 'order-last'
            : 'order-first',
          rakuten:
            this.isBenefitDisabled(this.rakutenBenefit) ||
            !this.$userStore.getClaimedCode(this.rakutenBenefit)
              ? 'order-last'
              : 'order-first',
        }
      }

      return {
        vue: 'hidden',
        rakuten: 'hidden',
      }
    },
    movieBenefits(): Array<Benefit> {
      const movieBenefits = []

      const vue = this.benefitsStore.benefits.get(BenefitCodes.Vue)

      if (vue) {
        movieBenefits.push(vue)
      }

      const rakuten = this.benefitsStore.benefits.get(BenefitCodes.Rakuten)

      if (rakuten) {
        movieBenefits.push(rakuten)
      }

      return movieBenefits
    },
    isMemberszoneReferrer(): boolean {
      return (
        (this.$router.options.history.state.back as string)?.includes(
          'members-zone'
        ) ?? false
      )
    },
  },
  watch: {
    // TODO Review
    '$userStore.claimedCodes.length': {
      handler() {
        this.$forceUpdate()
      },
      deep: true,
    },
  },
  async mounted() {
    await Promise.all([
      this.benefitsStore.fetchBenefits(),
      this.benefitsStore.fetchPosters(),
    ])
  },
  methods: {
    getBenefitsByCategory(category: TBenefitsCategory): Array<Benefit> {
      return this.benefitsStore.benefitsByCategory(category)
    },
    /**
     * Disables a benefit if the user has already claimed
     * a code of the same type and subtype in the same month.
     *
     * @param {Benefit} benefit
     * @return {boolean}
     */
    isBenefitDisabled(benefit: Benefit): boolean {
      if (['movies', 'tickets'].includes(benefit.redemption_category)) {
        const categoryClaimed = this.$userStore.claimedCodes
          .filter((claimedCode: ClaimedCode) =>
            ['movies', 'tickets'].includes(claimedCode.type)
          )
          .map((claimedCode: ClaimedCode) => claimedCode.type)

        if (!categoryClaimed.length) {
          return false
        }

        return !categoryClaimed.includes(benefit.redemption_category)
      }

      const categoryClaimed = this.$userStore.claimedCodes
        .filter(
          (claimedCode: ClaimedCode) =>
            claimedCode.type === benefit.redemption_category
        )
        .map((claimedCode: ClaimedCode) => claimedCode.subtype)

      if (!categoryClaimed.length) {
        return false
      }

      return !categoryClaimed.includes(benefit.redemption_subcategory ?? null)
    },
  },
})
</script>

<template>
  <AppBreadcrumb
    v-if="isMemberszoneReferrer"
    :to="$router.options.history.state.back ?? { name: 'member-zone' }"
  >
    {{ $router.currentRoute.value.meta.title }}
  </AppBreadcrumb>

  <AppSection>
    <h2 class="heading-2">
      {{
        $t(
          `section.benefits.discounts.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.heading`
        )
      }}
    </h2>
    <p>
      {{
        $t(
          `section.benefits.discounts.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.excerpt`
        )
      }}
    </p>

    <AppSlider slider-id="benefits-discount-slider" class="full-bleed-mobile">
      <BenefitCard
        v-for="benefit in getBenefitsByCategory(BenefitsCategories.Discounts)"
        :key="benefit.code"
        :benefit
      />
    </AppSlider>

    <template v-if="$userStore.isLoggedIn && tasteCardBenefit">
      <BenefitButtons
        :benefit="tasteCardBenefit"
        :disabled="isBenefitDisabled(tasteCardBenefit)"
        :claim-on-open="true"
      >
        Claim my Tastecard
      </BenefitButtons>
    </template>

    <AppMembershipButton v-if="$userStore.isLoggedOut" />
  </AppSection>

  <AppSection>
    <h2 class="heading-2">
      {{
        $t(
          `section.benefits.food_drinks.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.heading`
        )
      }}
    </h2>
    <p>
      {{
        $t(
          `section.benefits.food_drinks.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.excerpt`
        )
      }}
    </p>

    <AppSlider
      slider-id="benefits-food-and-drink-slider"
      class="full-bleed-mobile"
    >
      <BenefitCard
        v-for="benefit in getBenefitsByCategory(
          BenefitsCategories.FoodAndDrink
        ).sort((a, b) => (!isBenefitDisabled(a) ? -1 : 1))"
        :key="benefit.code"
        :benefit
        :disabled="isBenefitDisabled(benefit)"
      />
    </AppSlider>

    <AppMembershipButton v-if="$userStore.isLoggedOut" />
  </AppSection>

  <AppSection>
    <h2 class="heading-2">
      {{
        $t(
          `section.benefits.entertainment.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.heading`
        )
      }}
    </h2>
    <p>
      {{
        $t(
          `section.benefits.entertainment.${$userStore.isLoggedIn ? 'logged_in' : 'logged_out'}.excerpt`
        )
      }}
    </p>

    <AppSlider
      slider-id="benefits-entertainment-slider"
      class="full-bleed-mobile"
    >
      <BenefitCard
        v-if="vueBenefit"
        :benefit="vueBenefit"
        :disabled="isBenefitDisabled(vueBenefit)"
        :class="movieOrdering.vue"
      />

      <img
        v-for="(poster, key) in benefitsStore.posters"
        :key
        :src="poster.src"
        :alt="poster.alt"
        loading="lazy"
        class="aspect-9/16 !h-auto max-w-[288px] select-none rounded-lg object-cover hover:cursor-pointer"
      />

      <BenefitCard
        v-if="rakutenBenefit"
        :benefit="rakutenBenefit"
        :disabled="isBenefitDisabled(rakutenBenefit)"
        :class="movieOrdering.rakuten"
      />
    </AppSlider>

    <AppUpgradeModal
      v-if="$userStore.showUpgradePlan"
      :user-plan="$userStore.highestPlan"
      :upgrade-plan="
        $plansStore.getPlanByTier('vip', $userStore.highestPlan?.period)
      "
    />
  </AppSection>

  <HowItWorksSection />

  <PlansSection />

  <FaqSection :faq-content="faqStore.benefitFaq" />

  <TrustpilotBanner v-if="$userStore.isLoggedIn" class="!pb-12" />
</template>
